export function useCreateBooking() {
  const localePath = useLocalePath();
  const { newBooking } = usePostRequests();
  const businessStore = useBusinessStore();
  const {
    selectedLocation,
    booking,
    creatingNewBooking,
    availableCategories,
    selectedPaymentMethod,
    isNavigating,
    deal,
    couponApplied,
    giftCardCode,
    invoice,
    packageMemberEmployees,
  } = storeToRefs(businessStore);
  const toast = useToast();

  const handleNewBooking = async () => {
    try {
      creatingNewBooking.value = true;
      if (!selectedLocation.value?.identifier) return;
      const response = await newBooking(
        selectedLocation.value.identifier,
        prepareFormData()
      );

      if (response.code !== 200) return;

      isNavigating.value = true;

      toast.add({
        title: response.message,
        color: "green",
        icon: "i-approved-success",
      });

      if (response.data.redirect_url) {
        await navigateTo(response.data.redirect_url, { external: true });
        return;
      }

      booking.value.bookedServices.forEach((bookedService) => {
        const exists = availableCategories.value[0]?.services.some(
          (service) => service.id === bookedService.id
        );

        if (!exists) {
          availableCategories.value[0]?.services.push(bookedService);
        }
      });

      await navigateTo(
        localePath(
          `/${selectedLocation.value?.identifier}/b-${response.data.reference_id}/finished`
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      creatingNewBooking.value = false;
    }
  };

  /**
   * The placeholders the times page uses ("any professional", "auto select",
   * "one per service") are UI choices, not employees. The API knows only a
   * concrete id or the literal "any", so anything else collapses to "any" and
   * is resolved server side — for a package, to someone who performs all of it.
   */
  const PSEUDO_EMPLOYEE_IDS = new Set([
    "any",
    "auto_select",
    "employee_per_service",
  ]);

  const employeeIdForRequest = (employee?: { id: string | number } | null) => {
    const id = employee?.id;
    return id == null || PSEUDO_EMPLOYEE_IDS.has(String(id)) ? "any" : String(id);
  };

  const prepareFormData = () => {
    const formData = new FormData();
    formData.append("booking_date", booking.value.date);

    /**
     * Editing can unpack a package and reassign its member services, so those
     * professionals travel alongside the package itself, addressed by each
     * member's own booking service. A new booking treats the package as one
     * item under one professional and sends none.
     */
    if (booking.value.id) {
      Object.entries(packageMemberEmployees.value).forEach(
        ([bookingServiceId, employeeId]) =>
          formData.append(
            `package_member_employees[${bookingServiceId}]`,
            String(employeeId)
          )
      );
    }

    let serviceIndex = 0;
    let packageIndex = 0;
    booking.value.bookedServices.forEach((service) => {
      if (service.service_type === "package") {
        formData.append(
          `packages[${packageIndex}][package_id]`,
          service.id.toString()
        );
        formData.append(
          `packages[${packageIndex}][booking_time]`,
          service.selected_time ? service.selected_time : ""
        );
        formData.append(
          `packages[${packageIndex}][employee_id]`,
          employeeIdForRequest(service.selected_employee)
        );
        packageIndex++;
      } else {
        formData.append(
          `services[${serviceIndex}][service_id]`,
          service.id.toString()
        );
        formData.append(
          `services[${serviceIndex}][booking_time]`,
          service.selected_time ? service.selected_time : ""
        );
        formData.append(
          `services[${serviceIndex}][employee_id]`,
          employeeIdForRequest(service.selected_employee)
        );

        const serviceInfo = booking.value.selectedServices.find(
          (s) => s.id === service.id
        );
        if (
          serviceInfo &&
          serviceInfo.membership &&
          serviceInfo.membership.id
        ) {
          formData.append(
            `services[${serviceIndex}][client_membership_id]`,
            serviceInfo.membership.id.toString()
          );
        }
        serviceIndex++;
      }
    });

    if (
      selectedPaymentMethod.value?.key === "in_store" ||
      booking.value.subTotal === 0
    ) {
      formData.append("payment_method_id", selectedPaymentMethod.value?.id.toString());
    } else if (selectedPaymentMethod.value?.key === "gift-card") {
      formData.append("gift_card_code", giftCardCode.value);
      formData.append(
        "payment_method_id",
        selectedPaymentMethod.value?.id.toString()
      );
    } else {
      formData.append(
        "payment_method_id",
        selectedPaymentMethod.value?.id.toString() || ""
      );
    }

    if (selectedPaymentMethod.value?.key === "tap-cc")
      formData.append(
        "tap_token",
        selectedPaymentMethod.value?.tap_token as string
      );

    if (couponApplied.value && deal.value)
      formData.append("deal_id", deal.value.id.toString());

    if (selectedPaymentMethod.value?.accept_invoice && invoice.value)
      formData.append("invoice", invoice.value as File);

    return formData;
  };

  return {
    handleNewBooking,
    prepareFormData,
  };
}
